/**
 * frontend/src/components/VisorConciliacion.ts
 * ==============================================
 * El resultado de la conciliación, movimiento por movimiento, en la pantalla
 * de revisión.
 *
 * Por qué existe
 * --------------
 * El correo no puede ser la única forma de ver una conciliación: antes de
 * aprobar el envío, el contador revisa qué se apareó, qué quedó pendiente y
 * por qué. Las columnas son las de las hojas del Excel, y los datos salen del
 * mismo cálculo: lo que se aprueba acá es lo que llega por correo.
 *
 * Abre en la pestaña que más importa: Tipo A si hay pendientes en el banco,
 * Tipo B si los hay en contabilidad, y Conciliados si no queda nada por
 * revisar. Las tablas largas se muestran de a cien filas, y el buscador
 * filtra por descripción, asiento o importe.
 */

import { ApiError, descargarReporte, obtenerDetalle } from "../api";
import type { GrupoConciliado, MovimientoDetalle, ParConciliado, PeriodoDetalle } from "../types";
import { escapeHtml as _esc, fmtPesos } from "../utils/texto";
import { icon } from "../utils/icons";

type Pestana = "tipo_a" | "tipo_b" | "conciliados" | "grupos";

const PESTANAS: { clave: Pestana; titulo: string; ayuda: string }[] = [
  { clave: "tipo_a",      titulo: "Solo en banco",        ayuda: "Tipo A · requieren registro contable" },
  { clave: "tipo_b",      titulo: "Solo en contabilidad", ayuda: "Tipo B · verificar asiento, cheque o transferencia" },
  { clave: "conciliados", titulo: "Conciliados",          ayuda: "Un movimiento del banco con uno del libro" },
  { clave: "grupos",      titulo: "Agrupados",            ayuda: "Varios movimientos de un lado contra uno del otro" },
];

/** Filas por tanda: alcanza para revisar sin trabar la pantalla con libros grandes. */
export const FILAS_POR_TANDA = 100;

/**
 * Filas a la vista: con más, la tabla se desplaza por dentro. Fija el alto del
 * visor sin que una pestaña larga deje un hueco grande en las cortas.
 */
export const FILAS_VISIBLES = 10;

/** Alto de una fila vacía de relleno: el de una fila de una línea. */
const FILA_VACIA_PX = 40;

/** Minúsculas y sin tildes: «Depósito» se encuentra buscando «deposito». */
export function normalizar(texto: string): string {
  return texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/** Todo lo que el buscador mira de un movimiento, incluido el importe con y sin formato. */
function textoBuscable(...partes: (string | number | null | undefined)[]): string {
  return normalizar(partes.map((p) => {
    if (typeof p === "number") return `${fmtPesos(p)} ${p.toFixed(2)} ${p.toFixed(2).replace(".", ",")}`;
    return p ?? "";
  }).join(" "));
}

export function coincide(periodo: PeriodoDetalle, pestana: Pestana, consulta: string): unknown[] {
  const q = normalizar(consulta.trim());
  const filtrar = <T>(filas: T[], texto: (f: T) => string): T[] => (q ? filas.filter((f) => texto(f).includes(q)) : filas);

  switch (pestana) {
    case "tipo_a":
      return filtrar(periodo.tipo_a, (m) => textoBuscable(m.fecha, m.descripcion, m.monto));
    case "tipo_b":
      return filtrar(periodo.tipo_b, (m) => textoBuscable(m.fecha, m.asiento, m.descripcion, m.monto));
    case "conciliados":
      return filtrar(periodo.conciliados, (p) => textoBuscable(
        p.e_fecha, p.e_descripcion, p.e_monto, p.l_fecha, p.l_asiento, p.l_descripcion, p.l_monto));
    case "grupos":
      // Un grupo se muestra entero si cualquiera de sus movimientos coincide:
      // partido, no se entiende por qué cierra.
      return filtrar(periodo.grupos, (g) => textoBuscable(
        g.monto_total,
        ...[...g.extracto, ...g.libro].flatMap((m) => [m.fecha, m.asiento, m.descripcion, m.monto])));
  }
}

function cantidad(periodo: PeriodoDetalle, pestana: Pestana): number {
  return pestana === "grupos" ? periodo.grupos.length : periodo[pestana].length;
}

/** La primera pestaña con movimientos, en orden de lo que más importa revisar. */
function pestanaInicial(periodo: PeriodoDetalle): Pestana {
  return PESTANAS.find((t) => cantidad(periodo, t.clave) > 0)?.clave ?? "conciliados";
}

const importe = (valor: number | null | undefined): string =>
  valor === null || valor === undefined ? "" : _esc(fmtPesos(valor));

/**
 * Monta el visor en `contenedor` y carga el detalle.
 *
 * Si la carga falla, el visor lo dice y la decisión sigue disponible: no
 * poder ver el detalle no tiene por qué impedir aprobar o rechazar.
 */
export function montarVisor(contenedor: HTMLElement, threadId: string): void {
  contenedor.className = "visor";
  contenedor.innerHTML = `
    <div class="visor__cargando" role="status"><div class="spinner-inline"></div><p>Cargando los movimientos…</p></div>`;

  obtenerDetalle(threadId)
    .then(({ periodos }) => {
      if (!periodos.length) {
        contenedor.innerHTML = `<p class="visor__vacio">No hay movimientos para mostrar.</p>`;
        return;
      }
      dibujar(contenedor, threadId, periodos);
    })
    .catch((err) => {
      contenedor.innerHTML = `
        <p class="visor__aviso" role="alert">${icon("alert")} ${_esc(err instanceof ApiError
          ? err.message
          : "No se pudieron cargar los movimientos.")} Puede decidir igual con el resumen.</p>`;
    });
}

function dibujar(contenedor: HTMLElement, threadId: string, periodos: PeriodoDetalle[]): void {
  let indicePeriodo = 0;
  let pestana: Pestana = pestanaInicial(periodos[0]);
  let consulta = "";
  let visibles = FILAS_POR_TANDA;

  contenedor.innerHTML = `
    <div class="visor__barra">
      <h3 class="visor__titulo">Movimientos</h3>
      ${periodos.length > 1 ? `
        <select class="visor__periodo" aria-label="Período">
          ${periodos.map((p, i) => `<option value="${i}">${_esc(p.periodo)}</option>`).join("")}
        </select>` : ""}
      <label class="visor__buscar">
        ${icon("search")}
        <input type="search" placeholder="Buscar por descripción, asiento o importe" aria-label="Buscar movimientos" />
      </label>
      <div class="visor__descargas">
        <button class="btn btn--secondary btn--sm" type="button" data-formato="excel">${icon("download")} Excel</button>
        <button class="btn btn--secondary btn--sm" type="button" data-formato="pdf">${icon("download")} PDF</button>
      </div>
    </div>
    <div class="visor__pestanas" role="tablist" aria-label="Grupos de movimientos"></div>
    <div class="visor__panel" role="tabpanel"></div>
    <p class="visor__aviso" id="visor-error" role="alert" hidden></p>`;

  const pestanasEl = contenedor.querySelector<HTMLElement>(".visor__pestanas")!;
  const panel = contenedor.querySelector<HTMLElement>(".visor__panel")!;
  const buscador = contenedor.querySelector<HTMLInputElement>(".visor__buscar input")!;
  const errorEl = contenedor.querySelector<HTMLElement>("#visor-error")!;

  const pintarPestanas = (): void => {
    const p = periodos[indicePeriodo];
    // Una pestaña sin movimientos se ve apagada y no se abre: abrirla solo
    // mostraría un recuadro vacío.
    pestanasEl.innerHTML = PESTANAS.map((t) => `
      <button class="visor__pestana" type="button" role="tab" data-pestana="${t.clave}"
              aria-selected="${t.clave === pestana}" data-tooltip="${_esc(t.ayuda)}"
              ${cantidad(p, t.clave) === 0 ? "disabled" : ""}>
        <span class="visor__pestana-titulo">${t.titulo}</span>
        <span class="visor__pestana-cifras">${cantidad(p, t.clave)} · ${_esc(fmtPesos(p.totales[t.clave]))}</span>
      </button>`).join("");
    pestanasEl.querySelectorAll<HTMLButtonElement>("[data-pestana]").forEach((b) => b.addEventListener("click", () => {
      if (b.dataset.pestana === pestana) return;
      pestana = b.dataset.pestana as Pestana;
      visibles = FILAS_POR_TANDA;
      pintarPestanas();
      pintarPanel();
      aparecer();
    }));
  };

  /**
   * @param midiendo dibuja con el tope de diez filas para medir el alto que
   *   ocuparía la pestaña; sin él, la tabla llena el alto ya fijado.
   */
  const pintarPanel = (midiendo = false): void => {
    const filas = coincide(periodos[indicePeriodo], pestana, consulta);
    const mostradas = filas.slice(0, visibles);
    // Sin filas, la tabla igual se dibuja con su encabezado: el mensaje queda
    // dentro del recuadro y se lee como una tabla vacía, no como un hueco.
    const vacio = consulta.trim()
      ? `Ningún movimiento coincide con «${_esc(consulta.trim())}».`
      : "No hay movimientos en este grupo.";
    panel.innerHTML = `
      <div class="visor__tabla-wrap">${tabla(pestana, mostradas, vacio)}</div>
      <div class="visor__pie">
        <span class="visor__cuenta"></span>
        ${filas.length > visibles ? `<button class="btn btn--secondary btn--sm" type="button" id="visor-mas">Mostrar ${Math.min(FILAS_POR_TANDA, filas.length - visibles)} más</button>` : ""}
      </div>`;
    const wrap = panel.querySelector<HTMLElement>(".visor__tabla-wrap")!;
    const unidad = pestana === "grupos"
      ? (filas.length === 1 ? "grupo" : "grupos")
      : (filas.length === 1 ? "movimiento" : "movimientos");
    panel.querySelector(".visor__cuenta")!.textContent = `${filas.length} ${unidad}`;

    if (midiendo) {
      // El tope se mide sobre las filas dibujadas, no con un alto fijo: una
      // descripción larga ocupa dos líneas, y el corte tiene que caer entre
      // filas, no a mitad de una.
      const filasDom = wrap.querySelectorAll<HTMLElement>("tbody tr");
      wrap.style.maxHeight = filasDom.length > FILAS_VISIBLES ? `${filasDom[FILAS_VISIBLES].offsetTop}px` : "";
      return;
    }

    // Lo que sobra en el recuadro se completa con filas vacías: el blanco
    // conserva las líneas de la tabla, como una planilla, en vez de quedar
    // como un hueco sin forma.
    const tablaEl = wrap.querySelector<HTMLElement>("table")!;
    const libre = wrap.clientHeight - tablaEl.offsetHeight;
    const altoFila = wrap.querySelector<HTMLElement>("tbody tr")?.offsetHeight || 0;
    if (libre > 0 && altoFila > 0) {
      const columnas = tablaEl.querySelectorAll("col").length;
      const vacias = Math.floor(libre / Math.min(altoFila, FILA_VACIA_PX));
      tablaEl.querySelector("tbody:last-of-type")!.insertAdjacentHTML("beforeend",
        `<tr class="visor__fila-relleno" aria-hidden="true"><td colspan="${columnas}"></td></tr>`.repeat(vacias));
      // Lo que queda, menos que una fila, va a una última fila más baja: sin
      // ella quedaba una franja en blanco bajo las líneas.
      const resto = wrap.clientHeight - tablaEl.offsetHeight;
      if (resto > 0) {
        tablaEl.querySelector("tbody:last-of-type")!.insertAdjacentHTML("beforeend",
          `<tr class="visor__fila-relleno" aria-hidden="true"><td colspan="${columnas}" style="height: ${resto}px"></td></tr>`);
      }
    }

    panel.querySelector("#visor-mas")?.addEventListener("click", () => {
      visibles += FILAS_POR_TANDA;
      pintarPanel();
    });
  };

  /**
   * El alto del panel es el de la pestaña más alta del período, con el tope
   * de diez filas, y es el mismo para todas.
   *
   * Cada pestaña tiene otra cantidad de filas: si el panel tomara el alto de
   * la que se muestra, todo lo de abajo —los botones de la decisión— saltaría
   * con cada clic. Con el alto fijo, la tabla llena el recuadro: la larga se
   * desplaza por dentro y la corta se completa con filas vacías, sin dejar
   * espacio al pie. Se mide una vez por período, dibujando cada pestaña.
   */
  const fijarAlto = (): void => {
    const elegida = pestana;
    panel.style.height = "";
    let alto = 0;
    for (const { clave } of PESTANAS) {
      pestana = clave;
      pintarPanel(true);
      alto = Math.max(alto, panel.offsetHeight || 0);
    }
    pestana = elegida;
    if (alto) panel.style.height = `${alto}px`;
    pintarPanel();
  };

  /** Al cambiar de pestaña, el contenido aparece en vez de cortar de golpe. */
  const aparecer = (): void => {
    panel.classList.remove("visor__panel--aparece");
    void panel.offsetWidth; // reinicia la animación si se cambia rápido de pestaña
    panel.classList.add("visor__panel--aparece");
  };

  contenedor.querySelector<HTMLSelectElement>(".visor__periodo")?.addEventListener("change", (e) => {
    indicePeriodo = Number((e.target as HTMLSelectElement).value);
    pestana = pestanaInicial(periodos[indicePeriodo]);
    visibles = FILAS_POR_TANDA;
    consulta = "";
    buscador.value = "";
    pintarPestanas();
    fijarAlto();
    aparecer();
  });

  buscador.addEventListener("input", () => {
    consulta = buscador.value;
    visibles = FILAS_POR_TANDA;
    pintarPanel();
  });

  contenedor.querySelectorAll<HTMLButtonElement>("[data-formato]").forEach((boton) => {
    boton.addEventListener("click", async () => {
      const formato = boton.dataset.formato as "excel" | "pdf";
      const original = boton.innerHTML;
      boton.disabled = true;
      boton.textContent = "Preparando…";
      errorEl.hidden = true;
      try {
        const { contenido, nombre } = await descargarReporte(threadId, formato);
        const url = URL.createObjectURL(contenido);
        const a = document.createElement("a");
        a.href = url;
        a.download = nombre;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        errorEl.innerHTML = `${icon("alert")} ${_esc(err instanceof ApiError ? err.message : "No se pudo preparar el archivo.")}`;
        errorEl.hidden = false;
      } finally {
        boton.disabled = false;
        boton.innerHTML = original;
      }
    });
  });

  pintarPestanas();
  fijarAlto();
}

// ── Tablas ─────────────────────────────────────────────────────────────────────
//
// Todas las columnas tienen ancho fijo, el mismo en las cuatro pestañas, y el
// ancho que sobra lo toma una columna vacía al final. Con anchos según el
// contenido, cada pestaña repartía distinto y al cambiar todo saltaba de lugar;
// y si el texto se quedaba con el sobrante, el importe terminaba al otro lado
// de la pantalla de lo que describe.

type Columna = "fecha" | "asiento" | "texto" | "importe" | "lado";

const colgroup = (columnas: Columna[]): string =>
  `<colgroup>${columnas.map((c) => `<col class="visor__col--${c}">`).join("")}<col></colgroup>`;

/** La celda de relleno que cierra cada fila: lleva la línea hasta el borde. */
const RELLENO_TH = `<th aria-hidden="true"></th>`;
const RELLENO_TD = `<td aria-hidden="true"></td>`;

/** El cuerpo de la tabla, o una fila con el mensaje si no hay filas. */
const cuerpo = (filas: string[], columnas: number, vacio: string): string =>
  filas.length ? filas.join("") : `<tr class="visor__fila-vacia"><td colspan="${columnas + 1}">${vacio}</td></tr>`;

function tabla(pestana: Pestana, filas: unknown[], vacio: string): string {
  switch (pestana) {
    case "tipo_a":
      return tablaSimple(["fecha", "texto", "importe"], ["Fecha", "Descripción", "Importe"],
        (filas as MovimientoDetalle[]).map((m) => [_esc(m.fecha), _esc(m.descripcion), importe(m.monto)]), vacio);
    case "tipo_b":
      return tablaSimple(["fecha", "asiento", "texto", "importe"], ["Fecha", "Asiento", "Detalle", "Importe"],
        (filas as MovimientoDetalle[]).map((m) => [_esc(m.fecha), _esc(m.asiento ?? ""), _esc(m.descripcion), importe(m.monto)]), vacio);
    case "conciliados":
      return tablaConciliados(filas as ParConciliado[], vacio);
    case "grupos":
      return tablaGrupos(filas as GrupoConciliado[], vacio);
  }
}

function tablaSimple(columnas: Columna[], encabezados: string[], filas: string[][], vacio: string): string {
  const clase = (i: number) => (columnas[i] === "importe" ? ' class="visor__num"' : "");
  return `
    <table class="visor__tabla">
      ${colgroup(columnas)}
      <thead><tr>${encabezados.map((h, i) => `<th scope="col"${clase(i)}>${h}</th>`).join("")}${RELLENO_TH}</tr></thead>
      <tbody>${cuerpo(filas.map((f) => `<tr>${f.map((c, i) => `<td${clase(i)}>${c}</td>`).join("")}${RELLENO_TD}</tr>`), columnas.length, vacio)}</tbody>
    </table>`;
}

function tablaConciliados(pares: ParConciliado[], vacio: string): string {
  return `
    <table class="visor__tabla">
      ${colgroup(["fecha", "texto", "importe", "fecha", "asiento", "texto", "importe"])}
      <thead>
        <tr class="visor__lados"><th colspan="3" scope="colgroup">Banco</th><th colspan="4" scope="colgroup" class="visor__lado">Libro mayor</th>${RELLENO_TH}</tr>
        <tr>
          <th scope="col">Fecha</th><th scope="col">Descripción</th><th scope="col" class="visor__num">Importe</th>
          <th scope="col" class="visor__lado">Fecha</th><th scope="col">Asiento</th><th scope="col">Detalle</th><th scope="col" class="visor__num">Importe</th>${RELLENO_TH}
        </tr>
      </thead>
      <tbody>${cuerpo(pares.map((p) => `
        <tr>
          <td>${_esc(p.e_fecha)}</td><td>${_esc(p.e_descripcion)}</td><td class="visor__num">${importe(p.e_monto)}</td>
          <td class="visor__lado">${_esc(p.l_fecha)}${p.tolerancia
            ? ` <span class="visor__marca" data-tooltip="Apareado con diferencia de fecha, dentro de la tolerancia">±</span>` : ""}</td>
          <td>${_esc(p.l_asiento)}</td><td>${_esc(p.l_descripcion)}</td><td class="visor__num">${importe(p.l_monto)}</td>${RELLENO_TD}
        </tr>`), 7, vacio)}
      </tbody>
    </table>`;
}

function tablaGrupos(grupos: GrupoConciliado[], vacio: string): string {
  const filas = (lado: string, movs: MovimientoDetalle[]) => movs.map((m, i) => `
    <tr>
      <td class="visor__origen">${i === 0 ? lado : ""}</td>
      <td>${_esc(m.fecha)}</td><td>${_esc(m.asiento ?? "")}</td><td>${_esc(m.descripcion)}</td><td class="visor__num">${importe(m.monto)}</td>${RELLENO_TD}
    </tr>`).join("");
  return `
    <table class="visor__tabla">
      ${colgroup(["lado", "fecha", "asiento", "texto", "importe"])}
      <thead><tr><th scope="col">Origen</th><th scope="col">Fecha</th><th scope="col">Asiento</th><th scope="col">Descripción</th><th scope="col" class="visor__num">Importe</th>${RELLENO_TH}</tr></thead>
      ${grupos.length ? grupos.map((g, i) => `
        <tbody class="visor__grupo">
          <tr class="visor__grupo-titulo"><th colspan="4" scope="rowgroup">Grupo ${i + 1} · ${_esc(g.tipo)}</th><td class="visor__num">${importe(g.monto_total)}</td>${RELLENO_TD}</tr>
          ${filas("Banco", g.extracto)}${filas("Libro", g.libro)}
        </tbody>`).join("") : `<tbody>${cuerpo([], 5, vacio)}</tbody>`}
    </table>`;
}
